import { createEffect, untrack } from "solid-js";

/**
 * Resets the scroll container to the top whenever the "base" pathname changes.
 *
 * "Base" pathname strips the `/todo/:id` segment so that opening/closing a
 * todo detail drawer — which only appends `/todo/<id>` to the current path —
 * does NOT trigger a scroll reset.  Only a genuine navigation between views
 * (e.g. `/lists/abc` → `/lists/xyz`, or `/lists/abc` → `/`) resets the
 * scroll position.
 *
 * Using `on(..., { defer: true })` ensures the effect never fires on the
 * initial mount, avoiding the spurious "jump to top" that occurred when
 * PowerSync queries settled and caused a reactive re-evaluation.
 */

function getBasePath(pathname: string): string {
  // Remove query parameters, hashes, trailing slashes, and /todo/<id> overlay route suffix
  const pathOnly = (pathname.split("?")[0] ?? pathname).split("#")[0] ?? pathname;
  return pathOnly.replace(/\/todo\/[^/]+\/?$/, "").replace(/\/$/, "");
}

interface UsePathnameScrollResetOptions {
  /** Reactive accessor returning the current pathname. */
  pathnameAccessor: () => string;
  /** Reactive accessor returning the scroll container element. */
  containerAccessor: () => HTMLElement | null;
}

export const usePathnameScrollReset = (options: UsePathnameScrollResetOptions) => {
  const basePath = () => getBasePath(options.pathnameAccessor());
  let prevBasePath: string | undefined;

  createEffect(() => {
    const current = basePath();
    if (prevBasePath === undefined) {
      prevBasePath = current;
      return;
    }
    if (current !== prevBasePath) {
      prevBasePath = current;
      untrack(() => {
        const el = options.containerAccessor();
        if (el) {
          el.scrollTop = 0;
        }
      });
    }
  });
};
